
import React, { createContext, useContext, useState } from "react";
import { Answer, OrganizationDetails } from "@/types/assessment";
import { questions } from "@/data/questions";

interface AssessmentContextType {
  currentQuestionIndex: number;
  answers: Answer[];
  showResults: boolean;
  organizationDetails: OrganizationDetails | null;
  handleOrganizationSubmit: (details: OrganizationDetails) => void;
  handleAnswer: (value: number) => void;
  getCurrentAnswer: () => number | undefined;
  handleNext: () => void;
  handlePrevious: () => void;
  handleSkipToResults: () => void;
  clearSession: () => void;
}

const AssessmentContext = createContext<AssessmentContextType | undefined>(undefined);

export const AssessmentProvider = ({ children }: { children: React.ReactNode }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [organizationDetails, setOrganizationDetails] = useState<OrganizationDetails | null>(null);

  const handleOrganizationSubmit = (details: OrganizationDetails) => {
    if (!details.name || !details.industry || !details.size) {
      return;
    }
    setOrganizationDetails(details);
  };

  const handleAnswer = (value: number) => {
    const newAnswers = [...answers];
    const existingAnswerIndex = newAnswers.findIndex(
      (a) => a.questionId === questions[currentQuestionIndex].id
    );

    if (existingAnswerIndex >= 0) {
      newAnswers[existingAnswerIndex].value = value;
    } else {
      newAnswers.push({
        questionId: questions[currentQuestionIndex].id,
        value: value,
      });
    }

    setAnswers(newAnswers);
  };

  const getCurrentAnswer = () => {
    const answer = answers.find(
      (a) => a.questionId === questions[currentQuestionIndex].id
    );
    return answer?.value;
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      setShowResults(true);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleSkipToResults = () => {
    setShowResults(true);
  };

  const clearSession = () => {
    setCurrentQuestionIndex(0);
    setAnswers([]);
    setShowResults(false);
    setOrganizationDetails(null);
  };

  return (
    <AssessmentContext.Provider
      value={{
        currentQuestionIndex,
        answers,
        showResults,
        organizationDetails,
        handleOrganizationSubmit,
        handleAnswer,
        getCurrentAnswer,
        handleNext,
        handlePrevious,
        handleSkipToResults,
        clearSession,
      }}
    >
      {children}
    </AssessmentContext.Provider>
  );
};

export const useAssessment = () => {
  const context = useContext(AssessmentContext);
  if (context === undefined) {
    throw new Error("useAssessment must be used within an AssessmentProvider");
  }
  return context;
};
